import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions } from '@next/core-lib/site';
import { TableColumn, TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';

import { selectCropAids } from '../../crop-aid/store/crop-aid.selectors';
import {
  getCropYear,
  getReportSort,
  selectLoggedInEmployeeAsArray,
  selectSkalReport,
} from '../../store/selectors';
import { defaultReportTableConfig } from '../reportDefaults';
import { ReportSortService } from '../services/report-sort.service';

interface SkalTable {
  cropFieldName: string;
  cropName: string;
  area: number;
  tableConfig: TableConfig;
}

@Component({
  selector: 'app-skal-report',
  templateUrl: './skal-report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class SkalReportComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  protected readonly router = inject(Router);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  cropYear$ = this.store.pipe(select(getCropYear));
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  cropAids$ = this.store.pipe(select(selectCropAids));
  pageTitle$ = combineLatest([this.cropYear$, this.customer$]).pipe(
    map(
      ([cropYear, customer]) =>
        `${customer?.name ? customer.name : ''} ${this.locale.translate('skalReport')} ${cropYear}`,
    ),
  );
  skalReport$ = this.store.pipe(select(selectSkalReport));
  skalReportCropFields$ = this.skalReport$.pipe(
    filter((report) => report != null),
    map((report) => report.cropFields),
  );
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));

  tableConfigDefaultColumns: TableColumn[] = [
    {
      id: 'title',
      label: this.locale.translate('fertilizer'),
      type: TableColumnTypes.STRING,
    },
    {
      id: 'a',
      label: this.locale.translate('aKgNHa'),
      type: TableColumnTypes.NUMBER,
      digitsInfo: '1.0-0',
    },
    {
      id: 'b',
      label: this.locale.translate('bKgNHa'),
      type: TableColumnTypes.NUMBER,
      digitsInfo: '1.0-0',
    },
  ];
  tableConfigDefault: TableConfig = {
    ...defaultReportTableConfig,
    columns: this.tableConfigDefaultColumns,
  };
  tableConfigTotal: TableConfig = {
    ...this.tableConfigDefault,
    columns: [
      {
        id: 'title',
        label: '%A',
        type: TableColumnTypes.STRING,
      },
      {
        id: 'a',
        label: `A (${this.locale.translate('kgN')})`,
        type: TableColumnTypes.NUMBER,
      },
      {
        id: 'b',
        label: `B (${this.locale.translate('kgN')})`,
        type: TableColumnTypes.NUMBER,
      },
    ],
  };
  skalTables: SkalTable[] = [];

  reportSort$ = this.store.pipe(select(getReportSort));

  buttonItems$ = this.sortService.getButtonItems();

  private readonly subscription = new Subscription();

  ngOnInit() {
    this.subscription.add(
      this.pageTitle$.subscribe((pageTitle) => {
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
      }),
    );

    this.subscription.add(
      this.skalReport$.subscribe((skalReport) => {
        if (!skalReport) {
          return;
        }
        this.tableConfigTotal.dataSource.data = [
          {
            title: Math.round(skalReport.percentageSkalA),
            a: Math.round(skalReport.totalSkalA),
            b: Math.round(skalReport.totalSkalB),
          },
        ];
      }),
    );

    this.subscription.add(
      combineLatest([this.skalReportCropFields$, this.cropAids$, this.reportSort$]).subscribe(
        ([cropFields, cropAids, reportSort]) => {
          const skalTables: SkalTable[] = [];

          const sortedCropFields = this.sortService.sort({
            elements: cropFields,
            option: reportSort.option,
            descending: reportSort.descending,
            cropFieldNameFunc: (f) => f.name,
            cropNameFunc: (f) => f.crop,
          });

          sortedCropFields.forEach((cropField) => {
            const dataSource: { title: string; a: number; b: number }[] = [];
            cropField.skalItems.forEach((skalItem) =>
              dataSource.push({
                title: cropAids.find((cropAid) => cropAid.id === skalItem.cropAidId)?.name,
                a: Math.round(skalItem.skalANitrogen),
                b: Math.round(skalItem.skalBNitrogen),
              }),
            );

            const tableConfig = { ...this.tableConfigDefault };
            tableConfig.dataSource = new MatTableDataSource(dataSource);

            const skalTable: SkalTable = {
              cropFieldName: cropField.name,
              cropName: cropField.crop,
              area: cropField.areaInHectares,
              tableConfig,
            };
            skalTables.push(skalTable);
          });
          this.skalTables = skalTables;
        },
      ),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }
}
